import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ApiRoutes } from '@sb/library/api';
import { ChipButtonSelectData, FormBase, IconItem, Optional, VariantManager } from '@sb/library/base';
import { CalendarBookingDisplayItem, CalendarBookingSearchOptions, DisplayItemCollection, EventColorEnum, PricePerParticipantUnit, TagDisplayItem, TeacherNameDisplayItem, TimeZoneInfoDisplayItem, TimeZoneSearchOptions } from '@sb/library/data-models';
import { FormFieldBaseComponent } from '@sb/library/form-field';
import { SBDate, SBDateTime } from '@sb/library/localization';
import { StyleManager } from '@sb/library/managers';
import { Observable } from 'rxjs';

class SignUpSlotPickerForm extends FormBase {
    Slot = Optional(SBDateTime);
}

@Component({
    selector: 'lib-sign-up-slot-picker',
    templateUrl: './sign-up-slot-picker.component.html',
    styleUrls: ['./sign-up-slot-picker.component.scss'],
})
export class SignUpSlotPickerComponent extends FormFieldBaseComponent<CalendarBookingDisplayItem | null> implements AfterViewInit {
    readonly form = SignUpSlotPickerForm.Create();

    @ViewChild ('slotPicker', {static: false}) slotPickerRef!: ElementRef<HTMLElement>;

    @Input() schoolID!: string;
    @Input() websiteBlockID!: string;
    @Input() allowTimezones: boolean = false;

    @Input() teachers!: TeacherNameDisplayItem[];
    @Input() eventCategoryIDs: string[] | null = null;

    private _preFetchedSlots?: {
        SubjectTag?: TagDisplayItem;
        Category?: string;
        TeacherID?: string;
        BookingSlots: CalendarBookingDisplayItem[];
    };
    @Input()
    set preFetchedSlots (value: {
        SubjectTag?: TagDisplayItem;
        Category?: string;
        TeacherID?: string;
        BookingSlots: CalendarBookingDisplayItem[];
    }) {
        if (value && value.BookingSlots) {
            this._prefetchedBookingSlots = value.BookingSlots;
            if (this.teachers && this.teachers.length) {
                const teacherIds = new Set(this.teachers.map(t => t.ID));
                this._prefetchedBookingSlots = this._prefetchedBookingSlots.filter(slot => (slot.TeacherID && teacherIds.has(slot.TeacherID)) || (slot.OriginalTeacherID && teacherIds.has(slot.OriginalTeacherID)));
            }

            this.TeacherChange(value.TeacherID ?? '');
        }

        this._preFetchedSlots = value;
        //When prefetched booking slots refreshes/changes on adding additional student we reset SetUpBookingSlots 
        if (this._isBookingWidgetVisible && value?.BookingSlots) {
            this._filteredBookingSlots = [];
            this.formControl.setValue(null);
            this.SetupBookingSlots(value.BookingSlots);
        }
    }

    @Input() existingEvents: boolean | null = false;
    private _studentIDs: string[] = [];
    @Input()
    set studentIDs(value: string[]) {
        this._studentIDs = value;
        //When the studentIDs changes we need to refetch the booking slots
        if (this._isBookingWidgetVisible && this._bookingSlots !== undefined && value.length) {
            this._filteredBookingSlots = [];
            this.formControl.setValue(null);
            this.FetchBookingSlots();
        }
    }
    @Input() advanceBookingLimitDays: number = 0;

    @Input()
    set currentTimezone(timezone: TimeZoneInfoDisplayItem | undefined) {
        this._currentTimezone = timezone;
    }

    @Input() rescheduleEventID: string | null = null; 

    @Output() noSlotsAvailable: EventEmitter<boolean> = new EventEmitter();
    @Output() slotClicked: EventEmitter<void> = new EventEmitter();

    @Input() makeupCreditRequiredOnly: boolean = false;
    
    // if this._StudentPortalSettings.settings.StudentPortalSignupMakeupRequired is true => MakeupCreditRequired: true,
    // if public booking/websiteblockID is valid =? MakeupCreditRequired: false,
    // if this._StudentPortalSettings.settings.StudentPortalSignupMakeupRequired is false => MakeupCreditRequired: null

    private _loading: boolean = true;
    private _bookingSlots: CalendarBookingDisplayItem[] = [];
    private _availableBookingSlots: CalendarBookingDisplayItem[] = [];
    private _filteredBookingSlots: CalendarBookingDisplayItem[] = [];
    private _teacherChipData: ChipButtonSelectData<string>[] = [];
    private _currentTeacherSelected: string = '';
    private _currentSlotSelected!: CalendarBookingDisplayItem | null;
    private _currentDateSelected!: SBDateTime;
    private _isExpanded: boolean = false;
    private _currentTimezone?: TimeZoneInfoDisplayItem;
    private _timeZones: TimeZoneInfoDisplayItem[] = [];
    private _minDate: SBDate = SBDate.Today();
    private _maxDate: SBDate = this._minDate.Clone().AddDays(91);

    private _prefetchedBookingSlots: CalendarBookingDisplayItem[] = [];

    private _isBookingWidgetVisible: boolean = false; // we only want to call the api - when the booking widget is visible since we fetch everything now
    private _availableTeachers: TeacherNameDisplayItem[] = [];

    constructor(private _StyleManager: StyleManager,
        private _VariantManager: VariantManager
    ) {
        super();
        this.viewControl = new FormControl();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this._maxDate = this._minDate.Clone().AddDays(this.advanceBookingLimitDays + 1);

        this._prefetchedBookingSlots = this._preFetchedSlots?.BookingSlots!;
    }

    ngAfterViewInit(): void {
        if (this.slotPickerRef) {
            const observer = new IntersectionObserver(
                entries => {
                entries.forEach(entry => {
                    if (!this._isBookingWidgetVisible && entry.isIntersecting) {
                        this._isBookingWidgetVisible = true;
                        if (this._preFetchedSlots) {
                            if (this.allowTimezones) {
                                ApiRoutes.Localization.TimeZones.GetAll.Call().subscribe((data: TimeZoneInfoDisplayItem[]) => {
                                        this._timeZones = data;
                                        if (this.currentTimezone?.ID) {
                                            this.SetupBookingSlots(this._prefetchedBookingSlots);
                                            this._loading = false;
                                        } else {
                                            const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                            if (!timeZoneName) {
                                                this.SetupBookingSlots(this._prefetchedBookingSlots);
                                                this._loading = false;
                                                return;
                                            }
                                            ApiRoutes.Localization.TimeZones.Search.Call({
                                                Body: new TimeZoneSearchOptions({
                                                    TimeZoneID: timeZoneName
                                                })
                                            }).subscribe((data: TimeZoneInfoDisplayItem) => {
                                                this._currentTimezone = data;
                                                this.SetupBookingSlots(this._prefetchedBookingSlots);
                                                this._loading = false;
                                            });
                                        }
                                    });
                            } else {
                                this.SetupBookingSlots(this._prefetchedBookingSlots);
                                this._loading = false;
                            }

                        } else {
                            this.FetchData();
                        }
                    }
                });
                },
                { threshold: 0.1 }
            );
            observer.observe(this.slotPickerRef.nativeElement);
        }
    }

    GetCurrentTeacherSelected(value: string): ChipButtonSelectData<string> {
        return this.teacherChipData.find(item => item.Value == value)!;
    }

    TeacherChange(id: string): void {
        this._currentTeacherSelected = id;
        this.ApplyFilters();
    }

    UpdateBookingVisibility(): void {
        this._isBookingWidgetVisible = false;
    }

    DateTimeChanged(event: SBDateTime): void {
        this._currentDateSelected = event;
        this._filteredBookingSlots = [...this._availableBookingSlots].filter(slot => slot.StartDate === event.ToISO());
        if (this._filteredBookingSlots.length > 1) {
            this.SlotSelectionChange(null);
        } else {
            this.SlotSelectionChange(this._filteredBookingSlots[0]);
        }

    }

    BackgroundColorClass(color: EventColorEnum): string {
        return this._StyleManager.GetBackgroundColorCssClass(color);
    }

    SlotSelectionChange(slot: CalendarBookingDisplayItem | null): void {
        this._currentSlotSelected = slot;
        this.formControl.setValue(slot);
        if (slot !== null) {
            this.formControl.isDirty = true;
        }
    }

    SlotClicked(slot: CalendarBookingDisplayItem): void {
        this.SlotSelectionChange(slot);
        this.slotClicked.emit();
    }

    ShowMoreClicked(): void {
        this._isExpanded = !this._isExpanded;
    }

    TimeZoneChanged(timeZone: TimeZoneInfoDisplayItem): void {
        this._loading = true;
        this._currentTimezone = timeZone;
        this.FetchBookingSlots();
    }

    FetchBookingSlots(): void {
        this._loading = true;
        this.GetBookingSlotsRoute().subscribe(data => {
            this.SetupBookingSlots(data.ItemSubset);
            this._loading = false;
        });
    }

    private SetupBookingSlots(slots: CalendarBookingDisplayItem[]): void {
        this._teacherChipData = [];
        this._teacherChipData.push({
            Value: '',
            Label: $localize`:@@CommonSignUpSlotPickerAnyTeacher:Any #Teacher#`,
            HintMessage: null,
            IconName: null,
        });

        this._bookingSlots = slots;
        this._availableBookingSlots = this._bookingSlots;
        this.noSlotsAvailable.emit(this._bookingSlots.length === 0);
        const teacherMap = new Map<string, TeacherNameDisplayItem>();
        this._availableBookingSlots.forEach(b => {
            if (b.OriginalTeacher && !teacherMap.has(b.OriginalTeacher!.ID!)) {
                teacherMap.set(b.OriginalTeacher.ID!, new TeacherNameDisplayItem({
                    ID: b.OriginalTeacher!.ID,
                    Name: b.OriginalTeacher!.Name
                }));
            }
        });
        this._availableTeachers = Array.from(teacherMap.values()).orderByKey('Name');
        this.PrepareTeacherData();
    }

    override ObservePristine(): void {}

    private GetBookingSlotsRoute(): Observable<DisplayItemCollection<CalendarBookingDisplayItem>> {
        const body = new CalendarBookingSearchOptions({
            ExistingEvents: this.existingEvents,
            TeacherIDs: this.teachers.map(t => t.ID!),
            StartDate: this._minDate.ToISO(),
            AllDay: false,
            EndDate: this._minDate.Clone().AddDays(this.advanceBookingLimitDays + 1).ToISO(),
            EventCategoryIDs: this.eventCategoryIDs!,  // We know this is string | null; TypeWriter types are annoying here
            StudentIDs: this.studentIDs,
            MakeUpCreditRequired: this.makeupCreditRequiredOnly,
            RescheduleEventID: this.rescheduleEventID
        });

        if (this.isViewOnly || (!this.schoolID && !this.websiteBlockID)) {
            return ApiRoutes.Calendar.BookingSlots.GetAll.Call({
                Body: body,
                ExtraQueryParameters: { TimeZone: this._currentTimezone?.ID! }
            });
        }
        return ApiRoutes.Calendar.BookingSlots.Get.Call({
            Parameter1: this.schoolID,
            Parameter2: this.websiteBlockID,
            Body: body,
            ExtraQueryParameters: { TimeZone: this._currentTimezone?.ID! }
        })
    }

    private ApplyFilters(): void {
        let filterSlots: CalendarBookingDisplayItem[] = [];

        if (!this._currentTeacherSelected) {
            filterSlots = this._bookingSlots;
        } else {
            filterSlots = this._bookingSlots.filter(slot => slot.TeacherID === this._currentTeacherSelected);
        }

        if (this._preFetchedSlots?.Category) {
            filterSlots = filterSlots.filter(slot => slot.EventCategory?.Name == this._preFetchedSlots?.Category);
        }

        this._availableBookingSlots = filterSlots;

        // Reset
        this._filteredBookingSlots = [];

        if (this._currentDateSelected) {
            this.DateTimeChanged(this._currentDateSelected);
        }
    }

    private PrepareTeacherData(): void {
        this._availableTeachers.forEach(teacher => {
            this._teacherChipData.push({
                Value: teacher.ID!,
                Label: teacher.Name!,
                HintMessage: null,
                IconName: null,
            });
        });

        this.TeacherChange(this._currentTeacherSelected ?? '');
    }

    private FetchData(): void {
        if (this.allowTimezones) {
            ApiRoutes.Localization.TimeZones.GetAll.Call().subscribe((data: TimeZoneInfoDisplayItem[]) => {
                    this._timeZones = data;
                    if (this.currentTimezone?.ID) {
                        this.FetchBookingSlots();
                    } else {
                        const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
                        if (!timeZoneName) {
                            this.FetchBookingSlots();
                            return;
                        }
                        ApiRoutes.Localization.TimeZones.Search.Call({
                            Body: new TimeZoneSearchOptions({
                                TimeZoneID: timeZoneName
                            })
                        }).subscribe((data: TimeZoneInfoDisplayItem) => {
                            this._currentTimezone = data;
                            this.FetchBookingSlots();
                        });
                    }
                });
        } else {
            this.FetchBookingSlots();
        }
    }

    get loading(): boolean {
        return this._loading;
    }

    get bookingSlots(): CalendarBookingDisplayItem[] {
        return this._bookingSlots;
    }

    get availableBookingSlots(): CalendarBookingDisplayItem[] {
        return this._availableBookingSlots;
    }

    get filteredBookingSlots(): CalendarBookingDisplayItem[] {
        return this._filteredBookingSlots;
    }

    get visibleBookingSlots(): CalendarBookingDisplayItem[] {
        return this._isExpanded ? this._filteredBookingSlots : this._filteredBookingSlots.slice(0, 2);
    }

    get teacherChipData(): ChipButtonSelectData<string>[] {
        return this._teacherChipData;
    }

    get currentTeacherSelected(): string {
        return this._currentTeacherSelected;
    }

    get currentSlotSelected(): CalendarBookingDisplayItem | null {
        return this._currentSlotSelected;
    }

    get isExpanded(): boolean {
        return this._isExpanded;
    }

    get minDate(): SBDate {
        return this._minDate;
    }

    get maxDate(): SBDate {
        return this._maxDate;
    }

    get PricePerParticipantUnit(): typeof PricePerParticipantUnit {
        return PricePerParticipantUnit;
    }

    get subjectIcon(): IconItem {
        return this._VariantManager.subjectIconName;
    }

    get currentTimezone(): TimeZoneInfoDisplayItem | undefined {
        return this._currentTimezone;
    }

    get studentIDs(): string[] {
        return this._studentIDs;
    }

    get isAvailabilityBookingWidget(): boolean {
        return this.existingEvents === false;
    }
    get timeZones(): TimeZoneInfoDisplayItem[] {
        return this._timeZones;
    }

    get availableTeachers(): TeacherNameDisplayItem[] {
        return this._availableTeachers;
    }

    get isRescheduling(): boolean {
        return !!this.rescheduleEventID;
    }
}
